import { ProjectTabKeys } from '@common/project/constants';
import { QueryKeys } from '@common/queries/common';
import {
  ProjectAboutQuery,
  ProjectAboutQueryResponse,
  SqlProjectAboutQuery,
  SqlProjectAboutQueryResponse
} from '@common/queries/project';
import { Private_Routes } from '@common/url';
import { BackEndCodeLanguage, BackEndCodeLanguageType } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Stack, StackItem } from '@prophecy/ui-v3';
import { SQL_PROVIDER } from 'frontend/core/src/constants/sql';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { useNavigate } from 'react-router-dom';

import { useGraphQlQuery } from '../../../../data/util';
import { DescriptionSection } from './Components/DescriptionSection';
import { EditableReadme } from './Components/Readme';
import {
  ContentCountSection,
  ContentCountSectionProps,
  LanguageSection,
  LanguageSectionProps,
  ProjectUpdateSection,
  ProjectUpdateSectionProps,
  ReleaseTagsSection,
  VersionTagsSection
} from './Components/RightSectionComponents';
import { RightSectionRoot, StyledSeparator } from './Components/styled';
import { StatusSection } from './StatusSection';

export function ProjectInfo({
  language,
  projectId,
  isMainBranch,
  queriedBranch: _queriedBranch,
  isSimpleVersionControl
}: {
  queriedBranch?: string;
  language: BackEndCodeLanguageType;
  isMainBranch: boolean;
  projectId: string;
  isSimpleVersionControl: boolean;
}) {
  const isSqlProject = language === BackEndCodeLanguage.sql;
  const { data: sqlProjectAboutData, isLoading: isSqlLoading } = useGraphQlQuery(
    [QueryKeys.Git, QueryKeys.Project, 'project-about', { projectId }],
    SqlProjectAboutQuery,
    { enabled: isSqlProject },
    {
      projectId
    }
  );
  const { data: sparkProjectAboutData, isLoading: isSparkLoading } = useGraphQlQuery(
    [QueryKeys.Git, QueryKeys.Project, 'project-about', { projectId }],
    ProjectAboutQuery,
    { enabled: !isSqlProject },
    {
      projectId
    }
  );
  const isSapEnabled = useGlobalVariables().isSapEnabled;
  const isLoading = isSqlProject ? isSqlLoading : isSparkLoading;
  const projectAboutData = isSqlProject ? sqlProjectAboutData : sparkProjectAboutData;
  const currentProjectStatus = isSqlProject ? sqlProjectAboutData?.Project?.currentProjectStatus : undefined;
  if (isLoading || !projectAboutData || !projectAboutData.Project) {
    return null;
  }

  const project = projectAboutData.Project;
  const { providerType, created, createdByFullName, forkMode, readme, description } = project;
  const { sqlMacros, sqlModels, sqlSeeds, sqlSources } = project as NonNullable<
    SqlProjectAboutQueryResponse['Project']
  >;
  const { pipelines, gems, subgraphs, datasets, jobs } = project as unknown as NonNullable<
    ProjectAboutQueryResponse['Project']
  >;

  return (
    <Stack direction='horizontal' gap={theme.spaces.x32} height='100%'>
      <StackItem grow='1' height='100%'>
        <EditableReadme allowEdit={!isMainBranch} projectId={projectId} value={readme} />
      </StackItem>

      <RightSection
        isMainBranch={isMainBranch}
        language={language}
        description={description}
        providerType={isSapEnabled ? undefined : (providerType as SQL_PROVIDER)}
        projectId={projectId}
        created={created}
        isSimpleVersionControl={isSimpleVersionControl}
        createdByFullName={createdByFullName ?? ''}
        forkMode={forkMode}
        jobs={jobs}
        sqlMacros={sqlMacros}
        sqlModels={sqlModels}
        sqlSeeds={sqlSeeds}
        sqlSources={sqlSources}
        pipelines={pipelines}
        gems={gems}
        subgraphs={subgraphs}
        datasets={datasets}
        currentProjectStatus={currentProjectStatus}
      />
    </Stack>
  );
}

type RightSectionProps = {
  projectId: string;
  description: string;
  isMainBranch: boolean;
};

function RightSection({
  projectId,
  description,
  isMainBranch,
  language,
  providerType,
  created,
  createdByFullName,
  forkMode,
  jobs,
  sqlMacros,
  sqlModels,
  sqlSeeds,
  sqlSources,
  pipelines,
  gems,
  subgraphs,
  datasets,
  isSimpleVersionControl,
  currentProjectStatus
}: RightSectionProps &
  LanguageSectionProps &
  ContentCountSectionProps &
  ProjectUpdateSectionProps & { isSimpleVersionControl: boolean; currentProjectStatus?: string }) {
  const navigate = useNavigate();
  const { isSapEnabled } = useGlobalVariables();

  return (
    <RightSectionRoot padding={theme.spaces.x24}>
      <DescriptionSection allowEdit={!isMainBranch} projectId={projectId} value={description} />
      {isSapEnabled && currentProjectStatus && (
        <>
          <StyledSeparator orientation='horizontal' />
          <StatusSection currentProjectStatus={currentProjectStatus} />
        </>
      )}
      <StyledSeparator orientation='horizontal' />
      <LanguageSection language={language} providerType={providerType} />
      <StyledSeparator orientation='horizontal' />
      <ContentCountSection
        jobs={jobs}
        sqlMacros={sqlMacros}
        sqlModels={sqlModels}
        sqlSeeds={sqlSeeds}
        sqlSources={sqlSources}
        pipelines={pipelines}
        gems={gems}
        subgraphs={subgraphs}
        datasets={datasets}
      />
      <StyledSeparator orientation='horizontal' />
      {isSimpleVersionControl ? (
        <VersionTagsSection
          projectId={projectId}
          onMoreClick={() =>
            navigate(Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.VersionHistory }))
          }
        />
      ) : (
        <ReleaseTagsSection
          onlySucceeded={false}
          projectId={projectId}
          onMoreClick={() =>
            navigate(Private_Routes.Project.tab.getUrl({ uid: projectId, tab: ProjectTabKeys.Releases }))
          }
        />
      )}
      <StyledSeparator orientation='horizontal' />
      <ProjectUpdateSection created={created} createdByFullName={createdByFullName} forkMode={forkMode} />
    </RightSectionRoot>
  );
}
